import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileDown, FileSpreadsheet } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Chargement, EnteteEcran, MessageErreur } from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampDate } from '../components/ui/ChampDate';
import { attributsOnglet, attributsPanneau, clavierOnglets } from '../components/ui/onglets-aria';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import type { ChoixPeriode } from '../lib/useDashboardDirection';
import type { IndicateurSynthese, TonEcart } from '../lib/useSynthesesMensuelles';
import {
  CRITERES_RAPPORT,
  adresseDeLaDemande,
  demandeDeLAdresse,
  exporterRapport,
  refusDemande,
  useRapport,
  type CelluleRapport,
  type ComparaisonRapport,
  type CritereRapport,
  type DemandeRapport,
  type LigneTableauRapport,
  type ReponseRapport,
  type SectionRapport,
  type TableauRapport,
} from '../lib/useRapports';

/**
 * « Rapports » de la direction (écran `dir-rapports`, TF-480 ; pas de
 * maquette, composé avec les éléments d'« Analytics & ROI » et de
 * l'absentéisme).
 *
 * La direction compose son rapport — période, période de comparaison,
 * critères, formation et formateur référent ; l'antenne est celle du
 * sélecteur — puis le génère : la demande s'écrit dans l'adresse, qu'un favori
 * garde. Chaque critère présente ses indicateurs comparés, puis ses tableaux
 * par formation, par formateur référent et, pour les ruptures et les
 * signatures, par motif et par document. Tout vient de l'API ; l'écran ne
 * recompte rien. Les exports Excel et PDF reprennent le rapport affiché.
 */

const LIBELLES_PERIODE: { valeur: ChoixPeriode; libelle: string }[] = [
  { valeur: 'MOIS', libelle: 'Mois en cours' },
  { valeur: 'MOIS_PRECEDENT', libelle: 'Mois précédent' },
  { valeur: 'TRIMESTRE', libelle: 'Trimestre en cours' },
  { valeur: 'ANNEE', libelle: 'Année civile' },
  { valeur: 'LIBRE', libelle: 'Période personnalisée' },
];

const LIBELLES_COMPARAISON: { valeur: ComparaisonRapport; libelle: string }[] = [
  { valeur: 'PRECEDENTE', libelle: 'Période précédente' },
  { valeur: 'LIBRE', libelle: 'Autre période' },
  { valeur: 'AUCUNE', libelle: 'Sans comparaison' },
];

const LIBELLES_CRITERE: Record<CritereRapport, string> = {
  CONTRATS: 'Contrats',
  IMMERSIONS: 'Immersions',
  SIGNATURES: 'Signatures',
  MAINTIENS: 'Maintiens en formation',
  RUPTURES: 'Ruptures',
  ABSENCES: 'Absences',
  RESULTATS: 'Résultats',
};

const TONS_ECART: Record<TonEcart, string> = {
  success: 'text-emerald-700',
  danger: 'text-red-700',
  neutral: 'text-codialis-gray',
};

const CHAMP = 'min-h-[44px] w-full min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm';

function jourFrancais(jour: string): string {
  return jour.split('-').reverse().join('/');
}

function heureDeMiseAJour(instant: string): string {
  return new Date(instant).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

function Groupe({ legende, children }: { legende: string; children: ReactNode }): JSX.Element {
  return (
    <fieldset className="min-w-0 space-y-2">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-codialis-gray">{legende}</legend>
      {children}
    </fieldset>
  );
}

function Composition({
  brouillon,
  changer,
  donnees,
  modifie,
  onGenerer,
}: {
  brouillon: DemandeRapport;
  changer: (modification: Partial<DemandeRapport>) => void;
  donnees: ReponseRapport | undefined;
  modifie: boolean;
  onGenerer: () => void;
}): JSX.Element {
  const refus = refusDemande(brouillon);
  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (refus === null) onGenerer();
  };
  const basculer = (critere: CritereRapport): void =>
    changer({
      criteres: brouillon.criteres.includes(critere)
        ? brouillon.criteres.filter((autre) => autre !== critere)
        : CRITERES_RAPPORT.filter((autre) => autre === critere || brouillon.criteres.includes(autre)),
    });
  const formations = donnees?.options.formations ?? [];
  const formateurs = donnees?.options.formateurs ?? [];

  return (
    <form onSubmit={soumettre} className="space-y-5 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display font-bold text-codialis-navy">Composer le rapport</h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Groupe legende="Période">
          <select
            aria-label="Période"
            value={brouillon.periode}
            onChange={(evenement) => {
              const periode = evenement.target.value as ChoixPeriode;
              // Des dates personnalisées partent de la période affichée.
              changer({
                periode,
                du: periode === 'LIBRE' && !brouillon.du ? (donnees?.periode.du ?? '') : brouillon.du,
                au: periode === 'LIBRE' && !brouillon.au ? (donnees?.periode.arreteeAu ?? '') : brouillon.au,
              });
            }}
            className={CHAMP}
          >
            {LIBELLES_PERIODE.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
          {brouillon.periode === 'LIBRE' && (
            <div className="flex flex-wrap items-center gap-2">
              <ChampDate
                libelle="Du"
                valeur={brouillon.du}
                max={brouillon.au || undefined}
                onChange={(du) => changer({ du })}
              />
              <ChampDate
                libelle="au"
                valeur={brouillon.au}
                min={brouillon.du || undefined}
                onChange={(au) => changer({ au })}
              />
            </div>
          )}
        </Groupe>

        <Groupe legende="Comparer à">
          <select
            aria-label="Comparer à"
            value={brouillon.comparaison}
            onChange={(evenement) => {
              const comparaison = evenement.target.value as ComparaisonRapport;
              changer({
                comparaison,
                cdu: comparaison === 'LIBRE' && !brouillon.cdu ? (donnees?.comparaison?.du ?? '') : brouillon.cdu,
                cau: comparaison === 'LIBRE' && !brouillon.cau ? (donnees?.comparaison?.au ?? '') : brouillon.cau,
              });
            }}
            className={CHAMP}
          >
            {LIBELLES_COMPARAISON.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
          {brouillon.comparaison === 'LIBRE' && (
            <div className="flex flex-wrap items-center gap-2">
              <ChampDate
                libelle="Du"
                valeur={brouillon.cdu}
                max={brouillon.cau || undefined}
                onChange={(cdu) => changer({ cdu })}
              />
              <ChampDate
                libelle="au"
                valeur={brouillon.cau}
                min={brouillon.cdu || undefined}
                onChange={(cau) => changer({ cau })}
              />
            </div>
          )}
        </Groupe>

        <label className="block min-w-0">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-codialis-gray">Formation</span>
          <select
            value={brouillon.formation}
            onChange={(evenement) => changer({ formation: evenement.target.value })}
            className={CHAMP}
          >
            <option value="">Toutes les formations</option>
            {formations.map((formation) => (
              <option key={formation.code} value={formation.code}>
                {formation.titre}
              </option>
            ))}
          </select>
        </label>

        <label className="block min-w-0">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-codialis-gray">
            Formateur référent
          </span>
          <select
            value={brouillon.formateur}
            onChange={(evenement) => changer({ formateur: evenement.target.value })}
            className={CHAMP}
          >
            <option value="">Tous les formateurs</option>
            {formateurs.map((formateur) => (
              <option key={formateur.id} value={formateur.id}>
                {formateur.nom}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Groupe legende="Critères">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {CRITERES_RAPPORT.map((critere) => (
            <label
              key={critere}
              className="flex min-h-[40px] min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-codialis-navy has-[:checked]:border-codialis-navy has-[:checked]:bg-codialis-sand"
            >
              <input
                type="checkbox"
                checked={brouillon.criteres.includes(critere)}
                onChange={() => basculer(critere)}
                className="h-4 w-4 shrink-0 accent-codialis-navy"
              />
              <span className="min-w-0">{LIBELLES_CRITERE[critere]}</span>
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <button
            type="button"
            onClick={() => changer({ criteres: [...CRITERES_RAPPORT] })}
            className="min-h-[36px] rounded-lg px-2 font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
          >
            Tout cocher
          </button>
          <button
            type="button"
            onClick={() => changer({ criteres: [] })}
            className="min-h-[36px] rounded-lg px-2 font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
          >
            Tout décocher
          </button>
        </div>
      </Groupe>

      <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="min-w-0 text-xs text-codialis-gray" aria-live="polite">
          {refus ??
            (modifie
              ? 'Choix modifiés : générez le rapport pour les appliquer.'
              : 'Le rapport affiché suit ces choix.')}
        </p>
        <BoutonPrincipal type="submit" disabled={refus !== null}>
          Générer le rapport
        </BoutonPrincipal>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Rapport
// ---------------------------------------------------------------------------

function Indicateur({ indicateur }: { indicateur: IndicateurSynthese }): JSX.Element {
  const { comparaison } = indicateur;
  return (
    <div className="min-w-0 rounded-xl bg-gray-50 p-4">
      <div className="text-xs uppercase tracking-wide text-codialis-gray">{indicateur.libelle}</div>
      <div className="font-display mt-1 break-words text-2xl font-extrabold text-codialis-navy">{indicateur.texte}</div>
      {comparaison && (
        <div className="mt-1 text-xs">
          {comparaison.ecart && <span className={`font-bold ${TONS_ECART[comparaison.ton]}`}>{comparaison.ecart}</span>}
          <span className="text-codialis-gray">
            {comparaison.ecart ? ' · ' : ''}
            {comparaison.texte}
          </span>
        </div>
      )}
      {indicateur.detail && <div className="mt-1 text-xs text-codialis-gray">{indicateur.detail}</div>}
    </div>
  );
}

function Valeur({ cellule, gras }: { cellule: CelluleRapport; gras: boolean }): JSX.Element {
  return (
    <td className={`whitespace-nowrap px-3 py-2 text-right ${gras ? 'font-bold' : ''} text-codialis-navy`}>
      {cellule.texte}
      {cellule.precedentTexte !== null && (
        <span className="block text-xs font-normal text-codialis-gray">
          <span className="sr-only">comparaison : </span>
          {cellule.precedentTexte}
        </span>
      )}
    </td>
  );
}

function Ligne({ ligne, gras = false }: { ligne: LigneTableauRapport; gras?: boolean }): JSX.Element {
  return (
    <tr>
      <th scope="row" className={`px-3 py-2 text-left ${gras ? 'font-bold' : 'font-medium'} text-codialis-navy`}>
        {ligne.libelle}
      </th>
      {ligne.cellules.map((cellule, rang) => (
        <Valeur key={rang} cellule={cellule} gras={gras} />
      ))}
    </tr>
  );
}

function Tableau({ tableau, comparaison }: { tableau: TableauRapport; comparaison: string | null }): JSX.Element {
  if (tableau.lignes.length === 0) {
    return <p className="py-3 text-sm text-codialis-gray">Rien sur ces périodes.</p>;
  }
  return (
    // Le tableau défile de côté au téléphone : la zone se prend au clavier pour défiler. `relative` y
    // retient les libellés `sr-only` des cellules, positionnés en absolu, qui débordaient sinon la page.
    <div className="relative overflow-x-auto" tabIndex={0} role="region" aria-label={tableau.titre}>
      <table className="w-full min-w-[480px] text-sm">
        <caption className="sr-only">
          {tableau.titre}
          {comparaison ? ` ; sous chaque valeur, celle de la comparaison (${comparaison})` : ''}
        </caption>
        <thead>
          <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-codialis-gray">
            <th scope="col" className="px-3 py-2 text-left font-semibold">
              {tableau.entete}
            </th>
            {tableau.colonnes.map((colonne) => (
              <th key={colonne.libelle} scope="col" className="px-3 py-2 text-right font-semibold">
                {colonne.libelle}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tableau.lignes.map((ligne) => (
            <Ligne key={ligne.cle} ligne={ligne} />
          ))}
        </tbody>
        {tableau.ensemble && (
          <tfoot className="border-t-2 border-gray-200">
            <Ligne ligne={tableau.ensemble} gras />
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Section({ section, comparaison }: { section: SectionRapport; comparaison: string | null }): JSX.Element {
  const [actif, setActif] = useState(section.tableaux[0]?.id ?? '');
  const idTitre = useId();
  const idPanneau = `rapport-${section.critere.toLowerCase()}`;
  const tableau = section.tableaux.find((candidat) => candidat.id === actif) ?? section.tableaux[0];
  const etiquette = `Découpage · ${section.titre}`;

  return (
    <section aria-labelledby={idTitre} className="min-w-0 space-y-4 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 id={idTitre} className="font-display text-lg font-bold text-codialis-navy">
        {section.titre}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {section.indicateurs.map((indicateur) => (
          <Indicateur key={indicateur.id} indicateur={indicateur} />
        ))}
      </div>
      {tableau && (
        <div className="space-y-2">
          <div
            role="tablist"
            aria-label={etiquette}
            onKeyDown={clavierOnglets(
              section.tableaux.map((candidat) => candidat.id),
              tableau.id,
              setActif,
            )}
            className="-mx-1 flex min-w-0 flex-nowrap gap-1.5 overflow-x-auto px-1 pb-0.5"
          >
            {section.tableaux.map((candidat) => (
              <button
                key={candidat.id}
                type="button"
                {...attributsOnglet(candidat.id, candidat.id === tableau.id, idPanneau)}
                onClick={() => setActif(candidat.id)}
                className={`min-h-[36px] shrink-0 rounded-lg px-3 py-2 text-xs font-bold transition [@media(pointer:coarse)]:min-h-[40px] ${
                  candidat.id === tableau.id
                    ? 'bg-codialis-navy text-white'
                    : 'bg-gray-50 text-codialis-gray hover:bg-gray-100'
                }`}
              >
                {candidat.titre}
              </button>
            ))}
          </div>
          <div {...attributsPanneau(idPanneau, tableau.id)}>
            <Tableau tableau={tableau} comparaison={comparaison} />
          </div>
        </div>
      )}
      <p className="text-xs text-codialis-gray">{section.regle}</p>
    </section>
  );
}

function Lecture({ donnees, actualisation }: { donnees: ReponseRapport; actualisation: boolean }): JSX.Element {
  const { periode, comparaison } = donnees;
  return (
    <p className="text-xs text-codialis-gray" aria-live="polite">
      <span className="font-semibold text-codialis-navy">Période : {periode.libelle}</span>
      {periode.arreteeAu < periode.au ? `, arrêtée au ${jourFrancais(periode.arreteeAu)}` : ''}
      {' · '}
      {comparaison ? `comparée à ${comparaison.libelle}` : 'sans comparaison'}
      {' · '}
      {donnees.antennes.length > 0
        ? `antenne${donnees.antennes.length > 1 ? 's' : ''} ${donnees.antennes.join(', ')}`
        : 'aucune antenne'}
      {donnees.formation ? ` · ${donnees.formation.titre}` : ''}
      {donnees.formateur ? ` · référent ${donnees.formateur.nom}` : ''}
      {' · '}
      {actualisation ? 'Actualisation…' : `Mis à jour à ${heureDeMiseAJour(donnees.calculeLe)}`}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

export function DirRapportsPage(): JSX.Element {
  const { site } = usePerimetre();
  const toast = useToast();
  const [adresse, setAdresse] = useSearchParams();
  const demande = demandeDeLAdresse(adresse);
  const cle = adresseDeLaDemande(demande).toString();
  const [brouillon, setBrouillon] = useState<DemandeRapport>(demande);
  const [exportEnCours, setExportEnCours] = useState<'xlsx' | 'pdf' | null>(null);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const { data, isLoading, isFetching, error } = useRapport(demande, site);

  // Un retour en arrière dans l'historique ramène le rapport et ses choix.
  useEffect(() => setBrouillon(demandeDeLAdresse(adresse)), [adresse]);

  const modifie = adresseDeLaDemande(brouillon).toString() !== cle;
  const changer = (modification: Partial<DemandeRapport>): void =>
    setBrouillon((avant) => ({ ...avant, ...modification }));

  const exporter = async (format: 'xlsx' | 'pdf'): Promise<void> => {
    setErreurExport(null);
    setExportEnCours(format);
    try {
      const { nomFichier } = await exporterRapport(format, demande, site);
      toast(`${nomFichier} téléchargé`);
    } catch (refus) {
      setErreurExport(refus instanceof ErreurApi ? refus.message : 'L’export n’a pas pu être produit.');
    } finally {
      setExportEnCours(null);
    }
  };
  const exportImpossible = exportEnCours !== null || !data || data.vide || isFetching;
  const comparaison = data?.comparaison?.libelle ?? null;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Rapports"
        sousTitre="Période, critères et filtres au choix · comparaison entre deux périodes · exports Excel et PDF"
        actions={
          <>
            <BoutonSecondaire onClick={() => void exporter('xlsx')} disabled={exportImpossible}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours === 'xlsx' ? 'Export…' : 'Excel'}
            </BoutonSecondaire>
            <BoutonSecondaire onClick={() => void exporter('pdf')} disabled={exportImpossible}>
              <FileDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours === 'pdf' ? 'Export…' : 'PDF'}
            </BoutonSecondaire>
          </>
        }
      />

      <BarreSites />

      <Composition
        brouillon={brouillon}
        changer={changer}
        donnees={data}
        modifie={modifie}
        onGenerer={() => setAdresse(adresseDeLaDemande(brouillon))}
      />

      {erreurExport && <MessageErreur message={erreurExport} />}
      {data && <Lecture donnees={data} actualisation={isFetching} />}
      {isLoading && <Chargement libelle="Calcul du rapport…" />}
      {!isLoading && error && (
        <MessageErreur message={error instanceof ErreurApi ? error.message : 'Impossible de calculer le rapport.'} />
      )}

      {data && (
        <div className={`space-y-6 transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
          {data.vide && (
            <p className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
              Rien n’est arrivé sur ces périodes pour ces critères et ces filtres : il n’y a rien à exporter.
            </p>
          )}
          {data.sections.map((section) => (
            <Section key={section.critere} section={section} comparaison={comparaison} />
          ))}
        </div>
      )}
    </div>
  );
}
